import { Tooltip } from '@mui/material';
import { ReactNode } from 'react';

const font = "'Manrope', sans-serif";

// The app's stat tile, as Advisor-Client draws it (Tradehaus .tx-vz-card): value first, its label beneath, then a
// hairline visual and a caption along the foot. Every admin page's KPI row uses this, so the figures read the same
// everywhere. A sheet: glass panel, top light, raised.
export const StatTile = ({
  value,
  unit,
  label,
  visual,
  caption,
  loading = false,
}: {
  value: ReactNode;
  /** a small unit after the figure ("active", "%") */
  unit?: string;
  label: string;
  /** a glanceable hairline visual (ShareStrip, ticks, dots); every stat carries one */
  visual?: ReactNode;
  /** the foot: one caption, or [left, right] */
  caption?: ReactNode | [ReactNode, ReactNode];
  loading?: boolean;
}) => (
  <div
    style={{
      background: 'var(--tx-glass-panel)',
      backgroundImage: 'var(--tx-surface-light)',
      boxShadow: 'var(--tx-shadow)',
      borderRadius: 24,
      padding: 20,
      minHeight: 150,
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
      minWidth: 0,
      boxSizing: 'border-box',
      fontFamily: font,
    }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span
        style={{
          fontSize: 40,
          fontWeight: 200,
          letterSpacing: '-0.05em',
          lineHeight: 1,
          color: 'var(--tx-text)',
          fontVariantNumeric: 'proportional-nums',
        }}>
        {loading ? '—' : value}
      </span>
      {unit && !loading && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)' }}>{unit}</span>}
    </div>
    <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--tx-muted)', marginTop: -8 }}>{label}</div>
    {(visual || caption) && (
      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {!loading && visual}
        {caption != null && !loading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 10,
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--tx-muted)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}>
            {Array.isArray(caption) ? (
              <>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{caption[0]}</span>
                <span>{caption[1]}</span>
              </>
            ) : (
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{caption}</span>
            )}
          </div>
        )}
      </div>
    )}
  </div>
);

export interface SharePart {
  label: string;
  color: string;
  value: number;
}

// A segmented hairline strip, Advisor-Client's: each part's share in its colour. With a total larger than the parts,
// the rest is a quiet track, so a single part reads as its share of the whole
export const ShareStrip = ({
  parts,
  total,
  format = (v) => String(v),
}: {
  parts: SharePart[];
  total?: number;
  format?: (v: number) => string;
}) => {
  const sum = parts.reduce((acc, p) => acc + p.value, 0);
  const whole = Math.max(total ?? sum, sum);
  const rest = whole - sum;
  return (
    <div
      style={{
        display: 'flex',
        gap: 2,
        height: 4,
        borderRadius: 2,
        overflow: 'hidden',
        background: whole ? 'transparent' : 'var(--tx-fill-2)',
      }}>
      {parts
        .filter((p) => p.value > 0)
        .map((p) => (
          <Tooltip key={p.label} title={`${p.label} · ${format(p.value)}`} placement="top">
            <div style={{ flex: p.value, background: p.color, cursor: 'help' }} />
          </Tooltip>
        ))}
      {rest > 0 && <div style={{ flex: rest, background: 'var(--tx-fill-2)' }} />}
    </div>
  );
};

// The KPI row: equal columns, the frame's 20px rhythm
export const StatRow = ({ children, columns }: { children: ReactNode; columns: number }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 20, marginBottom: 20 }}>
    {children}
  </div>
);
